<script setup lang="ts">
/*
* Компонент табличного отображения справочника групп исполнителей заявок.
*
* Основные функции:
* - Отображает список групп исполнителей с пагинацией и бесконечной подгрузкой
* - Поддерживает поиск по ключевому слову и сортировку
* - Предоставляет быстрые действия: создание, редактирование и удаление групп
* - Позволяет просматривать состав участников группы
* - Построен строго по эталонным паттернам PositionsTable
*/

// Импорты Vue и Vue Router
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

// Импорты Vuetify
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты хранилищ
import { useTicketSettingsStore } from '../stores/ticketSettingsStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты утилит и констант
import { formatDateOnly, getInitialsFullName } from '@/utils/formatters'

// Импорты типов
import type { ITicketWorkGroupItem, ITicketWorkGroupPerformer } from '../types/settings/ITicketCategorySettings'

// Импорты компонентов
import WorkGroupFormDialog from './settings/WorkGroupFormDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'

// Импорт Composable
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { TICKETS_ANCHORS, TICKETS_CONTEXTS } from '@/modules/onboarding/scenarios/tickets/anchors'
import { ERouteNames } from '@/router/ERouteNames'

defineEmits(['click:prev', 'click:next', 'click:first', 'click:last'])

// Инициализация утилит отображения Vuetify
const { smAndDown } = useDisplay()
const theme = useTheme()

// Инициализация хранилищ
const permissionsStore = usePermissionsStore()
const ticketSettingsStore = useTicketSettingsStore()
const feedbackStore = useFeedbackStore()
const router = useRouter()

// Онбординг заявок заходит сюда веткой из меню «Настройки» и возвращается обратно в таблицу
useOnboardingContext({
  names: [TICKETS_CONTEXTS.workGroups],
  root: () => document.body,
  actions: {
    backToTickets: async () => { await router.push({ name: ERouteNames.TICKETS }) },
  },
})

// Извлекаем Refs для двусторонней синхронизации пагинации и сортировки
const { currentWorkGroupsSortString, nextWorkGroupsCursor } = storeToRefs(ticketSettingsStore)
const { itemsPerPage } = useTableItemsPerPage('ticket_work_groups', 25)

// Синхронизация с хранилищем групп исполнителей
ticketSettingsStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  ticketSettingsStore.itemsPerPage = newVal
})

// Конфигурация полей фильтров
const filterConfigs: IFilterConfig[] = []

// Инициализация Composable
const tableLogic = useTableFilters<{ search: string }>({
  filters: computed(() => ticketSettingsStore.workGroupsFilters),
  fetchData: async (cursor?: string, limit?: number) => {
    await ticketSettingsStore.fetchWorkGroupsTable(cursor, limit)
  },
  currentSortString: currentWorkGroupsSortString,
  itemsPerPage,
  resetFilters: () => ticketSettingsStore.resetWorkGroupsFilters(),
  nextCursor: nextWorkGroupsCursor,
  loadedItemsCount: computed(() => ticketSettingsStore.workGroupsList.length),
  totalItemsCount: computed(() => ticketSettingsStore.totalWorkGroupsCount),
  sortMap: {
    name: 'name',
    created_at: 'created_at',
  },
  filterConfigs,
})

// Заголовки таблицы групп исполнителей с метаданными
const headers = computed(() => [
  { title: 'Группа исполнителей', key: 'name', sortable: true },
  { title: 'Участники', key: 'performers', sortable: false },
  { title: 'Описание', key: 'description', sortable: false },
  { title: 'Дата создания', key: 'created_at', sortable: true, maxWidth: '10rem' },
  { title: 'Действия', key: 'actions', sortable: false, maxWidth: '7rem', align: 'center' },
] as any[])

const displayedWorkGroups = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return ticketSettingsStore.workGroupsList.slice(start, end)
})

function formatPerformers(performers?: ITicketWorkGroupPerformer[]): string {
  if (!performers || !performers.length) return ''
  return performers
    .map(p => getInitialsFullName(p) || [p.last_name, p.first_name, p.middle_name].filter(Boolean).join(' ') || 'Сотрудник')
    .filter(Boolean)
    .join(', ')
}

/**
 * Свойства строк таблицы для обработки кликов
 */
const rowProps = (item: { item: ITicketWorkGroupItem }) => {
  return {
    onClick: () => openEditDialog(item.item),
  }
}

// Состояния диалогов и модалок
const isFormDialogOpen = ref(false)
const selectedGroupId = ref<number | string | null>(null)

const isConfirmDeleteDialogOpen = ref(false)
const groupToDelete = ref<ITicketWorkGroupItem | null>(null)

function openCreateDialog() {
  selectedGroupId.value = null
  isFormDialogOpen.value = true
}

function openEditDialog(item: ITicketWorkGroupItem) {
  selectedGroupId.value = item.id
  isFormDialogOpen.value = true
}

function openDeleteConfirm(item: ITicketWorkGroupItem) {
  groupToDelete.value = item
  isConfirmDeleteDialogOpen.value = true
}

async function handleConfirmDelete(confirmed: boolean) {
  isConfirmDeleteDialogOpen.value = false
  if (confirmed && groupToDelete.value) {
    await ticketSettingsStore.deleteWorkGroup(groupToDelete.value.id)
  }
  groupToDelete.value = null
}
</script>

<template>
  <header :class="{ 'd-flex flex-column gap-2 mb-4': smAndDown }">
    <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
      <VBtn
        v-if="permissionsStore.modules.ticket?.work_group_edit"
        :class="{ 'w-100': smAndDown }"
        variant="elevated"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="TICKETS_ANCHORS.workGroups.add"
        @click="openCreateDialog"
      >
        Добавить группу
      </VBtn>
    </div>
  </header>

  <div class="wrapper">
    <div>
      <div
        class="d-flex align-center gap-4"
        :class="[
          tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
          { 'flex-column': smAndDown },
        ]"
      >
        <div
          class="d-flex align-center gap-2 w-100"
          :class="{ 'flex-column': smAndDown }"
        >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0"
            :class="smAndDown ? 'w-100' : 'w-25'"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="tableLogic.applySearchFilter()"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            :class="{ 'w-100': smAndDown }"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="tableLogic.applySearchFilter()"
          />

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            :class="[
              { 'w-100': smAndDown },
              !smAndDown && 'ml-auto',
            ]"
            height="48"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :loading="feedbackStore.isGlobalLoading"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>
        </div>
      </div>

      <div
        v-if="tableLogic.activeFilterChips.value.length"
        class="filter-chip d-flex ml-4 align-center gap-2 py-3"
      >
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <VDataTableServer
        :data-onboarding="TICKETS_ANCHORS.workGroups.table"
        :items="displayedWorkGroups"
        :items-length="ticketSettingsStore.totalWorkGroupsCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :loading="feedbackStore.isGlobalLoading"
        loading-text=""
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <!-- Заголовки таблицы -->
        <template #[`header.name`]="{ column }">
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon('name')"
              :class="{
                'is-active': tableLogic.isSortActive('name'),
                'is-desc': tableLogic.isSortDesc('name'),
              }"
            />
          </div>
        </template>

        <template #[`header.performers`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`header.description`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`header.created_at`]="{ column }">
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon('created_at')"
              :class="{
                'is-active': tableLogic.isSortActive('created_at'),
                'is-desc': tableLogic.isSortDesc('created_at'),
              }"
            />
          </div>
        </template>

        <template #[`header.actions`]="{ column }">
          <div class="header-content text-center">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <!-- Ячейки данных -->
        <template #[`item.name`]="{ item }">
          <span>{{ item.name }}</span>
        </template>

        <template #[`item.performers`]="{ item }">
          <CollapsibleText
            v-if="item.performers?.length"
            :text="formatPerformers(item.performers)"
          />
          <span v-else>—</span>
        </template>

        <template #[`item.description`]="{ item }">
          <CollapsibleText
            v-if="item.description"
            :text="item.description"
          />
        </template>

        <template #[`item.created_at`]="{ item }">
          <span v-if="item.created_at">{{ formatDateOnly(item.created_at) }}</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex align-center gap-1">
            <VIconBtn
              v-if="permissionsStore.modules.ticket?.work_group_edit"
              :rounded="false"
              :size="32"
              icon="ri-edit-line"
              variant="text"
              @click.stop="openEditDialog(item)"
            />
            <VIconBtn
              v-if="permissionsStore.modules.ticket?.work_group_edit"
              :rounded="false"
              :size="32"
              icon="ri-delete-bin-3-line"
              variant="text"
              @click.stop="openDeleteConfirm(item)"
            />
          </div>
        </template>

        <!-- Футер пагинации -->
        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>

  <!-- Модалка создания/редактирования группы исполнителей -->
  <WorkGroupFormDialog
    v-model="isFormDialogOpen"
    :group-id="selectedGroupId"
  />

  <!-- Диалог подтверждения удаления -->
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDeleteDialogOpen"
    :confirmation-question="`Удалить группу исполнителей «${groupToDelete?.name || ''}»?`"
    confirmation-text="Это действие нельзя отменить."
    confirm-btn="Удалить"
    reject-btn="Отмена"
    @confirm="handleConfirmDelete"
  />
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.v-list) {
  padding: 0 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.text-italic {
  font-style: italic;
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 16px 16px 16px !important;
}
</style>
